import { toast } from '@/components/toast';
import styled from '@emotion/styled';
import CloseIcon from '@mui/icons-material/Close';
import { AutocompleteGetTagProps, useAutocomplete } from '@mui/material';
import { Editor } from '@tiptap/core';
import { useState } from 'react';
import { useController, useFormContext } from 'react-hook-form';
import { cleanScriptField, isValidScriptField } from '../scriptFieldUtils';

const StyledTag = styled.div`
    display: flex;
    align-items: center;
    height: 24px;
    line-height: 22px;
    background-color: ${({ theme }) => theme.colors.primary.bgLight};
    color: ${({ theme }) => theme.colors.primary.main};
    border-radius: 5px;
    box-sizing: content-box;
    padding: 0 2px 0 6px;
    outline: 0;
    overflow: hidden;
    cursor: pointer;

    & span {
        overflow: hidden;
        white-space: nowrap;
        text-overflow: ellipsis;
    }

    & svg {
        margin: 0 2px;
        font-size: 12px;
        padding: 2px;
        border-radius: 4px;

        :hover {
            background-color: ${({ theme }) => theme.colors.primary.main};
            color: white;
        }
    }
`;

interface TagProps extends ReturnType<AutocompleteGetTagProps> {
    label: string;
    setHoveredTag: (tag: string | null) => void;
    onClick: () => void;
}

const Tag = (props: TagProps) => {
    const { label, onDelete, setHoveredTag, onClick, ...other } = props;

    const handleMouseEnter = () => {
        setHoveredTag(label);
    };

    const handleMouseLeave = () => {
        setHoveredTag(null);
    };

    return (
        <StyledTag {...other} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
            <span
                onClick={(e: React.MouseEvent) => {
                    e.stopPropagation();
                    onClick();
                }}>
                {label}
            </span>
            <CloseIcon onClick={onDelete} />
        </StyledTag>
    );
};

const InputWrapper = styled.div`
    border: 1px solid ${({ theme }) => theme.colors.black[300]};
    background-color: white;
    border-radius: 8px;
    padding: 14px;
    display: flex;
    flex-wrap: wrap;
    gap: 8px;

    font-size: 16px;
    font-weight: 400;
    line-height: 24px;
    caret-color: ${({ theme }) => theme.colors.primary[900]};

    &.focused {
        border-color: ${({ theme }) => theme.colors.black[700]};
    }

    & input {
        outline: none;
        height: 24px;
        color: ${({ theme }) => theme.colors.secondary.main};
        box-sizing: border-box;
        width: 0;
        flex-grow: 1;
        border: 0;
        margin: 0;
    }
`;

interface Props {
    setHoveredTag: (tag: string | null) => void;
    editorRef: React.MutableRefObject<Editor | null>;
}

export const TagInput = ({ setHoveredTag, editorRef }: Props) => {
    const { control } = useFormContext();
    const [inputValue, setInputValue] = useState<string>('');
    const [toastId, setToastId] = useState<string | number | null>(null);
    const { field: scriptFieldsField } = useController({
        control,
        name: 'script_fields',
    });

    const { getRootProps, getInputProps, getTagProps, focused, setAnchorEl } = useAutocomplete<string, true, false, true>({
        id: 'customized-hook-demo',
        defaultValue: [],
        multiple: true,
        options: [],
        freeSolo: true,
        onChange: (_, values, reason, details) => {
            if (reason === 'removeOption' && !!details?.option) {
                editorRef.current?.commands.removeScriptField(details.option);
            }

            scriptFieldsField.onChange(values);
        },
        clearOnBlur: false,
        value: scriptFieldsField.value,
        onInputChange: (_, value) => setInputValue(value),
        inputValue,
    });

    const inputProps = getInputProps();
    return (
        <div {...getRootProps()}>
            <InputWrapper ref={setAnchorEl} className={focused ? 'focused' : ''} id="script-field-input">
                {scriptFieldsField.value.map((option: string, index: number) => {
                    const { key, ...tagProps } = getTagProps({ index });
                    return (
                        <Tag
                            label={option}
                            key={key}
                            {...tagProps}
                            setHoveredTag={setHoveredTag}
                            onClick={() => editorRef.current?.commands.injectScriptField(option)}
                        />
                    );
                })}
                <input
                    {...inputProps}
                    data-hj-allow=""
                    onKeyDown={e => {
                        if (e.key === 'Enter' || e.key === 'Tab') {
                            if (!inputValue) {
                                const id = toast.error({
                                    message: "Script fields can't be empty",
                                    prevId: toastId,
                                });
                                setToastId(id);
                            } else if (isValidScriptField(inputValue)) {
                                const cleanInputValue = cleanScriptField(inputValue);
                                const values = new Set([...scriptFieldsField.value, cleanInputValue]);
                                scriptFieldsField.onChange(Array.from(values));
                                setInputValue('');
                            } else {
                                const id = toast.error({
                                    message: 'Script fields can only contain alphanumeric characters and underscores',
                                    prevId: toastId,
                                });
                                setToastId(id);
                            }

                            e.preventDefault();
                            e.stopPropagation();
                            return;
                        }

                        inputProps.onKeyDown?.(e);
                    }}
                />
            </InputWrapper>
        </div>
    );
};
